import {
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  Link,
  useLocation,
} from "react-router-dom";

import siteConfig from "../../../site/config/site";
import featureConfig from "../../../site/config/features";
import navigationConfig from "../../../site/config/navigation";

import {
  findRouteByPath,
  getLanguageFromPath,
  getRoutePath,
} from "../../../site/config/routes";

import LanguageToggle from "../common/LanguageToggle";
import ThemeToggle from "../common/ThemeToggle";
import MobileMenu from "./MobileMenu";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  NAVBAR
  ============================================================

  Vastaa koko sivuston päänavigaatiosta:

  - brändi / logo
  - desktop-navigaatio
  - aktiivinen reitti
  - FI / EN
  - Ivory / Noir
  - mobiilin MENU-painike
  - fullscreen MobileMenu
  - hero-overlayn kontrasti

  Brändi muodostetaan siteConfigista.

  Jos:
  name = "Photography Studio"
  shortName = "Photography"

  navbar näyttää:

  Photography
  Studio

  eikä pitkää nimeä katkaista ellipsillä.
*/

export default function Navbar({
  theme,
  themes,
  setTheme,
}) {
  const {
    t,
  } = useTranslation("common");

  const location =
    useLocation();

  const [
    isMobileMenuOpen,
    setIsMobileMenuOpen,
  ] = useState(false);

  /*
    Sivuston kieli määräytyy URL:n perusteella.
  */

  const routeLanguage =
    getLanguageFromPath(
      location.pathname
    );

  /*
    Selvitetään nykyinen looginen sivu.
  */

  const currentRoute =
    findRouteByPath(
      location.pathname
    );

  /*
    Etusivun navbar tulee hero-median päälle.
  */

  const isHomeRoute =
    currentRoute?.id ===
    "home";

  /*
    Noir-herossa käytetään vaaleaa overlay-navigaatiota.
  */

  const isDarkHero =
    isHomeRoute &&
    theme === "noir";

  /*
    Ivory-etusivu saa kevyen frosted-navbarin.
  */

  const isIvoryHero =
    isHomeRoute &&
    theme === "ivory";

  /*
    ============================================================
    BRAND LOCKUP
    ============================================================

    Käytetään shortName-arvoa ensimmäisenä rivinä.

    Jos koko nimi alkaa shortName-arvolla,
    loppuosa näytetään pienempänä toisena rivinä.

    Esimerkki:
    Photography Studio
    ->
    Photography
    Studio
  */

  const brandName =
    String(
      siteConfig.brand?.name ||
      ""
    ).trim();

  const brandShortName =
    String(
      siteConfig.brand
        ?.shortName ||
      brandName
    ).trim();

  const brandSecondary =
    brandName
      .toLowerCase()
      .startsWith(
        brandShortName
          .toLowerCase()
      )
      ? brandName
          .slice(
            brandShortName.length
          )
          .trim()
      : "";

  return (
    <>
      {/* ======================================================
          NAVBAR
          ====================================================== */}

      <header
        className={`
          z-50
          w-full
          border-b
          transition-all
          duration-500

          ${
            isHomeRoute
              ? "absolute left-0 top-0"
              : "relative"
          }

          ${
            isDarkHero
              ? `
                border-white/20
                bg-black/5
                text-white
              `
              : isIvoryHero
                ? `
                  border-border/80
                  bg-canvas/75
                  text-text
                  shadow-[0_1px_0_rgba(0,0,0,0.015)]
                  backdrop-blur-md
                `
                : `
                  border-border
                  bg-canvas
                  text-text
                `
          }
        `}
      >
        <div
          className="
            mx-auto
            flex
            w-full
            max-w-editorial
            items-center
            justify-between
            gap-3
            px-6
            py-4

            sm:px-10

            lg:gap-4
            lg:px-10
            lg:py-5

            xl:px-16
            xl:py-6
          "
        >
          {/* ==================================================
              BRAND
              ================================================== */}

          <Link
            to={getRoutePath(
              "home",
              routeLanguage
            )}
            className="
              flex
              shrink-0
              flex-col
              justify-center
              no-underline
              transition-opacity
              duration-300
              hover:opacity-70
            "
            aria-label={
              routeLanguage ===
              "en"
                ? "Home"
                : "Etusivu"
            }
          >
            <span
              className="
                whitespace-nowrap
                font-display
                text-[15px]
                leading-none
                tracking-[0.06em]

                xl:text-[17px]
                xl:tracking-[0.08em]
              "
            >
              {brandShortName}
            </span>

            {brandSecondary && (
              <span
                className={`
                  mt-1
                  whitespace-nowrap
                  text-[8px]
                  font-medium
                  uppercase
                  leading-none
                  tracking-[0.3em]

                  xl:text-[9px]

                  ${
                    isDarkHero
                      ? "text-white/60"
                      : "text-muted"
                  }
                `}
              >
                {brandSecondary}
              </span>
            )}
          </Link>

          {/* ==================================================
              DESKTOP
              ================================================== */}

          <div
            className="
              hidden
              min-w-0
              items-center
              gap-x-3

              lg:flex

              xl:gap-x-4
            "
          >
            <nav
              aria-label={
                routeLanguage ===
                "en"
                  ? "Main navigation"
                  : "Päänavigaatio"
              }
              className="
                flex
                items-center
                gap-4

                xl:gap-5
              "
            >
              {navigationConfig.map(
                (item) => {
                  const itemPath =
                    getRoutePath(
                      item.routeId,
                      routeLanguage
                    );

                  const isActive =
                    currentRoute?.id ===
                    item.routeId;

                  return (
                    <Link
                      key={
                        item.id
                      }
                      to={
                        itemPath
                      }
                      className={
                        isDarkHero
                          ? isActive
                            ? `
                              whitespace-nowrap
                              border-b
                              border-white
                              pb-1
                              text-[10px]
                              uppercase
                              tracking-[0.14em]
                              text-white

                              xl:text-xs
                              xl:tracking-[0.16em]
                            `
                            : `
                              whitespace-nowrap
                              pb-1
                              text-[10px]
                              uppercase
                              tracking-[0.14em]
                              text-white/70
                              transition
                              duration-300
                              hover:text-white

                              xl:text-xs
                              xl:tracking-[0.16em]
                            `
                          : isIvoryHero
                            ? isActive
                              ? `
                                whitespace-nowrap
                                border-b
                                border-text
                                pb-1
                                text-[10px]
                                uppercase
                                tracking-[0.14em]
                                text-text

                                xl:text-xs
                                xl:tracking-[0.16em]
                              `
                              : `
                                whitespace-nowrap
                                pb-1
                                text-[10px]
                                uppercase
                                tracking-[0.14em]
                                text-text/75
                                transition
                                duration-300
                                hover:text-text

                                xl:text-xs
                                xl:tracking-[0.16em]
                              `
                            : isActive
                              ? `
                                whitespace-nowrap
                                border-b
                                border-text
                                pb-1
                                text-[10px]
                                uppercase
                                tracking-[0.14em]
                                text-text

                                xl:text-xs
                                xl:tracking-[0.16em]
                              `
                              : `
                                whitespace-nowrap
                                pb-1
                                text-[10px]
                                uppercase
                                tracking-[0.14em]
                                text-muted
                                transition
                                duration-300
                                hover:text-text

                                xl:text-xs
                                xl:tracking-[0.16em]
                              `
                      }
                    >
                      {t(
                        item.labelKey
                      )}
                    </Link>
                  );
                }
              )}
            </nav>

            {/* =================================================
                LANGUAGE
                ================================================= */}

            {featureConfig.languageSwitcher && (
              <LanguageToggle
                currentLanguage={
                  routeLanguage
                }
                currentRouteId={
                  currentRoute?.id ||
                  "home"
                }
                appearance={
                  isDarkHero
                    ? "overlay"
                    : "default"
                }
              />
            )}

            {/* =================================================
                THEME
                ================================================= */}

            {featureConfig.themeSwitcher && (
              <ThemeToggle
                theme={
                  theme
                }
                themes={
                  themes
                }
                setTheme={
                  setTheme
                }
                language={
                  routeLanguage
                }
              />
            )}
          </div>

          {/* ==================================================
              MOBILE / TABLET
              ================================================== */}

          <div
            className="
              flex
              shrink-0
              items-center
              gap-2
              lg:hidden
            "
          >
            {featureConfig.languageSwitcher && (
              <div
                className="
                  hidden
                  sm:block
                "
              >
                <LanguageToggle
                  currentLanguage={
                    routeLanguage
                  }
                  currentRouteId={
                    currentRoute?.id ||
                    "home"
                  }
                  appearance={
                    isDarkHero
                      ? "overlay"
                      : "default"
                  }
                />
              </div>
            )}

            {/* =================================================
                MOBILE MENU BUTTON
                ================================================= */}

            {featureConfig.mobileMenu && (
              <button
                type="button"
                onClick={() =>
                  setIsMobileMenuOpen(
                    true
                  )
                }
                className={
                  isDarkHero
                    ? `
                      group
                      flex
                      h-11
                      items-center
                      gap-3
                      rounded-full
                      border
                      border-white/30
                      bg-black/10
                      px-4
                      text-white
                      backdrop-blur-md
                      transition
                      duration-300
                      hover:border-white
                    `
                    : `
                      group
                      flex
                      h-11
                      items-center
                      gap-3
                      rounded-full
                      border
                      border-border
                      bg-surface/70
                      px-4
                      text-text
                      backdrop-blur-md
                      transition
                      duration-300
                      hover:border-text
                    `
                }
                aria-label={t(
                  "controls.openMenu"
                )}
                aria-expanded={
                  isMobileMenuOpen
                }
              >
                <span
                  className="
                    hidden
                    text-[10px]
                    font-medium
                    uppercase
                    tracking-[0.18em]

                    sm:inline
                  "
                >
                  {t(
                    "controls.menu"
                  )}
                </span>

                <span
                  className="
                    flex
                    w-[18px]
                    flex-col
                    gap-[5px]
                  "
                  aria-hidden="true"
                >
                  <span
                    className="
                      h-px
                      w-full
                      bg-current
                    "
                  />

                  <span
                    className="
                      h-px
                      w-[70%]
                      self-end
                      bg-current
                      transition-all
                      duration-300
                      group-hover:w-full
                    "
                  />
                </span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ======================================================
          FULLSCREEN MOBILE MENU
          ====================================================== */}

      {featureConfig.mobileMenu && (
        <MobileMenu
          isOpen={
            isMobileMenuOpen
          }
          onClose={() =>
            setIsMobileMenuOpen(
              false
            )
          }
          navigationItems={
            navigationConfig
          }
          language={
            routeLanguage
          }
          currentRouteId={
            currentRoute?.id ||
            "home"
          }
          theme={
            theme
          }
          themes={
            themes
          }
          setTheme={
            setTheme
          }
        />
      )}
    </>
  );
}